/**
 * The formatting commands behind the email body's toolbar.
 *
 * ---------------------------------------------------------------------------
 * WHY `execCommand`, WHICH IS DEPRECATED
 * ---------------------------------------------------------------------------
 * Because the alternative is a document model. Bold-the-selection is only easy
 * if something already knows how to split a text node across three elements,
 * merge the adjacent spans afterwards, and put the caret back — and that
 * something is either `execCommand` or a full editor framework. This body is a
 * few paragraphs with links and emphasis; it does not justify the framework.
 *
 * `execCommand` is deprecated but not going anywhere: every browser implements
 * it precisely because contenteditable is unusable without it. The risk is
 * accepted knowingly, and it is contained here — the editor calls these
 * functions and never touches `document.execCommand` itself.
 *
 * ---------------------------------------------------------------------------
 * EVERYTHING IS AN INLINE STYLE
 * ---------------------------------------------------------------------------
 * `styleWithCSS` is turned on so the browser emits `<span style="…">` instead
 * of `<font>` and `<b>`. That matters because this markup is read by a mail
 * client, which has no stylesheet of ours and inconsistent support for
 * presentational tags. Only the properties in `EMAIL_ALLOWED_CSS` survive
 * sanitization, so the toolbar and the sanitizer have to agree — they do,
 * by construction: every command below writes one of those properties.
 */

/** Body text, in the sizes an author actually reaches for. */
export const EMAIL_FONT_SIZES = [
  { label: "Small", px: 13 },
  { label: "Normal", px: 15 },
  { label: "Large", px: 18 },
  { label: "Title", px: 24 },
];

/**
 * A closed palette rather than a colour picker.
 *
 * An email that can be any of sixteen million colours is an email that stops
 * looking like it came from Flex. These are the console's own tiers plus the
 * accent, so a formatted body still reads as the same product.
 */
export const EMAIL_TEXT_COLORS = [
  { label: "Ink", value: "#1A1A18" },
  { label: "Graphite", value: "#4A4842" },
  { label: "Muted", value: "#6E6C66" },
  { label: "Accent", value: "#C24A0C" },
  { label: "Orange", value: "#FF6414" },
  { label: "Green", value: "#2F6F52" },
  { label: "Red", value: "#9A3B34" },
];

/** Highlights, in the same tinted-surface family the console uses for status. */
export const EMAIL_HIGHLIGHTS = [
  { label: "None", value: "transparent" },
  { label: "Sand", value: "#F2F1ED" },
  { label: "Peach", value: "#FFF0E8" },
  { label: "Wheat", value: "#F5F0E2" },
  { label: "Sage", value: "#E8F0EB" },
  { label: "Rose", value: "#F6E8E7" },
];

/**
 * What a legacy `<font size="1..7">` is worth in pixels.
 *
 * The scale browsers have used since Netscape. Needed because pasted mail —
 * from Outlook, from Gmail's older output — still arrives wrapped in `<font>`,
 * and `font` is not a tag the sanitizer keeps. Without this the paste would
 * silently lose every size it had.
 */
const FONT_TAG_PX = { 1: 10, 2: 13, 3: 15, 4: 18, 5: 24, 6: 32, 7: 48 };

/**
 * Is there a document to command?
 *
 * jsdom has no `execCommand`, and neither does a server render. Both are
 * environments where "apply bold" is meaningless rather than broken, so the
 * commands no-op instead of throwing.
 */
const canExec = () =>
  typeof document !== "undefined" && typeof document.execCommand === "function";

/**
 * Run one command against the current selection.
 *
 * @param {string} command
 * @param {string} [value]
 * @returns {boolean} Whether the command ran.
 */
const exec = (command, value) => {
  if (!canExec()) return false;
  try {
    return document.execCommand(command, false, value);
  } catch {
    // Some browsers throw on an unsupported command rather than returning
    // false. A formatting button that cannot format is not worth an exception.
    return false;
  }
};

/** Emit inline styles rather than `<font>`/`<b>`. See the header note. */
const emitInlineStyles = (on = true) => exec("styleWithCSS", String(on));

/**
 * The author's selection, if it is inside the body.
 *
 * Returns null for a selection somewhere else on the page — applying a
 * formatting command to text outside the editor is the failure this guards.
 *
 * @param {HTMLElement} root
 * @returns {Range|null}
 */
const rangeWithin = (root) => {
  if (typeof window === "undefined" || !window.getSelection) return null;
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return null;

  const range = selection.getRangeAt(0);
  if (!root || !root.contains(range.commonAncestorContainer)) return null;
  return range;
};

/** Put the selection back around a node, so a second pick hits the same words. */
const reselect = (node) => {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(node);
  selection.removeAllRanges();
  selection.addRange(range);
};

/**
 * Bold, italic or underline the selection.
 *
 * @param {"bold"|"italic"|"underline"} command
 * @returns {boolean}
 */
export const toggleInline = (command) => {
  emitInlineStyles(true);
  return exec(command);
};

/**
 * Is the selection already bold / italic / underlined?
 *
 * Drives the pressed state on the toolbar, so the buttons describe the caret
 * rather than just accepting clicks.
 *
 * @param {"bold"|"italic"|"underline"} command
 * @returns {boolean}
 */
export const isInlineActive = (command) => {
  if (typeof document === "undefined" || !document.queryCommandState) {
    return false;
  }
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
};

/** Colour the selection's text. @param {string} value @returns {boolean} */
export const applyTextColor = (value) => {
  emitInlineStyles(true);
  return exec("foreColor", value);
};

/**
 * Colour the selection's background.
 *
 * `hiliteColor` is the name Firefox knows; Chrome accepts it too and falls back
 * to `backColor`, which is the only name older WebKit implements.
 *
 * @param {string} value - A hex, or `transparent` to clear.
 * @returns {boolean}
 */
export const applyHighlight = (value) => {
  emitInlineStyles(true);
  return exec("hiliteColor", value) || exec("backColor", value);
};

/**
 * Resize.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS ONE DOES NOT USE `execCommand`
 * ---------------------------------------------------------------------------
 * It did, and it did not work. `execCommand("fontSize")` only speaks the legacy
 * 1–7 scale, so the trick was to ask for 7 and rewrite the resulting
 * `<font size="7">` into a real pixel span. Two things went wrong with that:
 *
 *  1. It wrapped nothing when the caret was collapsed. The browser records a
 *     *pending* style instead, then emits a fresh `<font>` around whatever gets
 *     typed next — long after the rewrite has run. That `<font>` is not in the
 *     sanitizer's allowlist, so the size vanished on its way to the preview.
 *  2. Even with a selection, any `<font>` produced later by continued typing
 *     met the same fate.
 *
 * Writing the span directly has neither problem: the markup is the final markup
 * the moment it is created.
 *
 * WITH A SELECTION it wraps those words. WITH A COLLAPSED CARET it sets the
 * body's base size — "make this email bigger", which is the only reading of a
 * size change with nothing selected that does anything at all.
 *
 * @param {HTMLElement} root - The editable element.
 * @param {number} px
 * @returns {boolean}
 */
export const applyFontSize = (root, px) => {
  if (!root) return false;

  const range = rangeWithin(root);
  if (!range || range.collapsed) {
    root.style.fontSize = `${px}px`;
    return true;
  }

  const span = document.createElement("span");
  span.style.fontSize = `${px}px`;
  span.appendChild(range.extractContents());

  // A size on a descendant beats the one just asked for, so the old sizes
  // inside the selection go. Other formatting — colour, weight — stays.
  span.querySelectorAll("[style]").forEach((node) => {
    node.style.fontSize = "";
    if (!node.getAttribute("style")) node.removeAttribute("style");
  });

  range.insertNode(span);
  reselect(span);
  return true;
};

/**
 * The pixel size at the caret, for ticking the right row in the size menu.
 *
 * @param {HTMLElement} root
 * @returns {number|null} Rounded px, or null when it cannot be measured.
 */
export const currentFontSize = (root) => {
  if (!root || typeof window === "undefined" || !window.getComputedStyle) {
    return null;
  }

  const range = rangeWithin(root);
  const node = range ? range.startContainer : root;
  const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  if (!element) return null;

  const size = parseFloat(window.getComputedStyle(element).fontSize);
  return Number.isFinite(size) ? Math.round(size) : null;
};

/**
 * Rewrite any `<font>` in the body into a span the sanitizer will keep.
 *
 * Nothing here produces `<font>` any more; paste does. Running this on every
 * edit is what stops a pasted email's sizes and colours from surviving in the
 * editor but disappearing from the preview and the send.
 *
 * Children are moved rather than re-created, so the caret — which lives in one
 * of those text nodes — stays exactly where the author left it.
 *
 * @param {HTMLElement} root
 * @returns {boolean} Whether anything was rewritten.
 */
export const normalizeFontTags = (root) => {
  const fonts = root ? root.querySelectorAll("font") : [];
  if (!fonts.length) return false;

  fonts.forEach((node) => {
    const span = document.createElement("span");
    const px = FONT_TAG_PX[node.getAttribute("size")];
    if (px) span.style.fontSize = `${px}px`;
    const color = node.getAttribute("color");
    if (color) span.style.color = color;

    while (node.firstChild) span.appendChild(node.firstChild);
    node.replaceWith(span);
  });

  return true;
};

/**
 * Break the line with a `<br>`.
 *
 * Enter in a `contentEditable` wraps each line in its own `<div>`, which is
 * fine in a browser and a lottery in a mail client — some collapse the margins,
 * some add their own, and a body written as ten divs arrives with ten different
 * gaps. A `<br>` means one thing everywhere.
 *
 * @param {HTMLElement} root
 * @returns {boolean}
 */
export const insertLineBreak = (root) => {
  if (exec("insertLineBreak")) return true;

  const range = rangeWithin(root);
  if (!range) return false;

  range.deleteContents();
  const br = document.createElement("br");
  range.insertNode(br);

  // A trailing `<br>` renders nothing — the browser needs something after it
  // before it will show the new line. A second one is that something, and it
  // is what the caret then sits before.
  const caretAnchor =
    br.nextSibling === null ? br.parentNode.appendChild(document.createElement("br")) : br;

  const next = document.createRange();
  next.setStartBefore(caretAnchor === br ? br.nextSibling : caretAnchor);
  next.collapse(true);

  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(next);
  return true;
};

/**
 * Strip formatting from the selection, leaving the words and any links.
 *
 * @returns {boolean}
 */
export const clearFormatting = () => {
  emitInlineStyles(true);
  return exec("removeFormat");
};
